import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE, normalizeLanguage, type SupportedLanguage } from '@shared/constants/languages.ts';
import { localizedOptionalText, localizedText, type LocalizedText } from '@shared/constants/localized-text.ts';

/**
 * Reading company *content* — room names, task names — in the reader's own
 * language.
 *
 * The distinction this file exists to keep: `t('roomsStep.roomNameLabel')`
 * translates the app's chrome from a bundled catalog, while these resolve a
 * text the company typed, which travels with its row and can only ever be in
 * the languages the company filled in (see localized-text.ts). Both follow the same language,
 * which is the whole point — a Spanish interface labeling an English room
 * name is exactly as half-translated as the reverse.
 *
 * Customer-facing documents do NOT go through here. The quote page, its PDF
 * and everything the company sends follow the *quote's* language and are
 * resolved in the Worker; this is the staff side, where the reader is whoever
 * is holding the screen.
 */

/**
 * The language to read company content in: the one the interface is in.
 *
 * `useTranslation` rather than `activeLanguage()` so a component re-renders
 * when the language resolves — detection settles inside `init()`, but a
 * `changeLanguage` from a test or a future switcher would otherwise leave
 * names in the previous language until something else forced a render.
 */
export function useContentLanguage(): SupportedLanguage {
  const { i18n } = useTranslation();
  return normalizeLanguage(i18n.resolvedLanguage ?? i18n.language) ?? DEFAULT_LANGUAGE;
}

export interface LocalizedReader {
  /** The language everything below resolves into. */
  language: SupportedLanguage;
  /** A required text — a room or task name — in the reader's language. */
  text: (base: string, translations: LocalizedText | null | undefined) => string;
  /** An optional one — a description — which may still be absent entirely. */
  optionalText: (base: string | null, translations: LocalizedText | null | undefined) => string | null;
}

/**
 * A reader fixed to one language, for text that belongs to a DOCUMENT rather
 * than to whoever is looking at it.
 *
 * The quote editor is the case: an estimator is authoring the page and the PDF
 * the customer will read, so the room and task names on it are the document's
 * content and follow `quote.language` — the same language the Worker renders
 * that document in. Showing them in the estimator's own language instead would
 * be a preview that lies, and worse: the room-name field is editable, so a
 * Spanish-reading estimator would type a Spanish name onto an English quote
 * without ever being shown the English one they were replacing.
 *
 * `useLocalizedContent` above is the other half — the library, the work order
 * and the schedule, where the reader is the audience.
 */
export function readerFor(language: SupportedLanguage): LocalizedReader {
  return {
    language,
    text: (base, translations) => localizedText(base, translations, language),
    optionalText: (base, translations) => localizedOptionalText(base, translations, language),
  };
}

export function useLocalizedContent(): LocalizedReader {
  const language = useContentLanguage();
  return useMemo(
    () => ({
      language,
      text: (base, translations) => localizedText(base, translations, language),
      optionalText: (base, translations) => localizedOptionalText(base, translations, language),
    }),
    [language],
  );
}
